import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { CheckIcon, PaperAirplaneIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { withArtikelImage } from "@/components/ArtikelImage";
import { Autocomplete } from "@/components/Autocomplete";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import { TextInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import {
  getRecentAuftraege,
  heroArticleOrderText,
  searchAuftraege,
  searchHeroArticles,
  type HeroArticleSuggestion,
} from "@/lib/material";
import { topOrderProducts } from "@/lib/orders";
import type { AuftragMini, HeroSupplier, HeroSupplierContact } from "@/lib/orders";
import { COMMON_MATERIAL_UNITS, resolveMaterialUnit } from "@/lib/materialUnits";
import { formatAuftragHeadline } from "@/lib/orderDraftUtils";
import type { OrderSupplierState } from "@/lib/orderDraftUtils";
import {
  COMPANY_EMAIL_CONTACT_LABEL,
  COMPANY_EMAIL_SALUTATION,
  loadOrderSupplierContactOptions,
  loadOrderSupplierOptions,
  supplierFieldsFromContact,
  supplierToAutocompleteOption,
} from "@/lib/orderSupplierAutocomplete";
import { listAnfrageSnippetTemplates, type AnfrageSnippetTemplate } from "@/lib/anfragen";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { MaWizardSplitLayout } from "@/pages/material/MaContextPanel";
import {
  anfrageSendLabel,
  createRecipient,
  newPositionLocalId,
  persistLocalDraft,
  validateTaRecipients,
  type AnfrageWizardState,
  type DraftPosition,
  type RecipientMode,
  type TaRecipient,
} from "@/pages/anfragen/anfrageWizardUtils";

/* ─────────────────────────── Helpers ─────────────────────────── */

function isValidEmailForGate(email: string): boolean {
  const t = (email || "").trim();
  if (!t.includes("@")) return false;
  const [local, domain] = t.split("@");
  if (!local || !domain || !domain.includes(".")) return false;
  return !/\s/.test(t);
}

function hasSupplierGate(recipients: TaRecipient[]): boolean {
  const r = recipients[0];
  if (!r) return false;
  if (r.mode === "hero") return !!(r.supplier?.id && String(r.supplier.id).trim());
  return isValidEmailForGate(r.manual.email);
}

function recipientToSupplierState(r: TaRecipient): OrderSupplierState | null {
  if (r.mode !== "hero" || !r.supplier) return null;
  const c = r.contact || {};
  return {
    id: r.supplier.id,
    name: r.supplier.name,
    company_email: r.supplier.email || "",
    email: c.email || "",
    contact_first_name: c.first_name || "",
    contact_last_name: c.last_name || "",
    anrede: c.anrede || "",
    person: [c.first_name, c.last_name].filter(Boolean).join(" ").trim(),
  };
}

function supplierOptionFromRecipient(r: TaRecipient) {
  if (r.mode !== "hero" || !r.supplier?.name) return null;
  return supplierToAutocompleteOption({
    id: r.supplier.id,
    name: r.supplier.name,
    email: r.supplier.email,
    geschaeftspartner_id: r.supplier.geschaeftspartner_id,
    has_logo: r.supplier.has_logo,
    logo_updated_at: r.supplier.logo_updated_at,
  });
}

function contactOptionFromRecipient(r: TaRecipient) {
  if (r.mode !== "hero") return null;
  const c = r.contact;
  if (!c?.email && !c?.first_name && !c?.last_name) return null;
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  const company =
    !!c.is_company_email || ((c.anrede || "").trim() === COMPANY_EMAIL_SALUTATION && !name);
  if (company && c.email) {
    return {
      key: `company-email-${c.email.toLowerCase()}`,
      label: COMPANY_EMAIL_CONTACT_LABEL,
      description: c.email,
      value: {
        label: COMPANY_EMAIL_CONTACT_LABEL,
        value: `company:${c.email.toLowerCase()}`,
        first_name: "",
        last_name: "",
        email: c.email,
        title: "",
        is_company_email: true,
      } as HeroSupplierContact,
    };
  }
  return {
    key: `con-${c.email || name}`,
    label: name || c.email || "Ansprechpartner",
    description: c.email || undefined,
    value: {
      label: name,
      value: c.email || name,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      email: c.email || "",
      title: c.title || c.anrede || "",
    } as HeroSupplierContact,
  };
}

function anredeFromTitle(title: string): string {
  const t = (title || "").toLowerCase();
  if (/frau/.test(t)) return "Frau";
  if (/herr/.test(t)) return "Herr";
  return "";
}

function recipientSummary(r: TaRecipient): { firm: string; contact: string } {
  if (r.mode === "hero") {
    const c = r.contact || {};
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
    return {
      firm: r.supplier?.name || "–",
      contact: [name, c.email].filter(Boolean).join(" · ") || "Kein Ansprechpartner",
    };
  }
  return {
    firm: r.manual.firm || "–",
    contact: [r.manual.name, r.manual.email].filter(Boolean).join(" · ") || "–",
  };
}

type WizardStep = 1 | 2 | 3;

const STEPS: { step: WizardStep; label: string; hint: string }[] = [
  { step: 1, label: "Lieferant", hint: "Wen fragen wir an?" },
  { step: 2, label: "Positionen", hint: "Was brauchen wir wofür?" },
  { step: 3, label: "Prüfen & senden", hint: "Mail ansehen und abschicken" },
];

interface BlechForm {
  material: string;
  dicke: string;
  laenge: string;
  breite: string;
  kanten: string;
}

const EMPTY_BLECH: BlechForm = { material: "Blech", dicke: "1.5", laenge: "1000", breite: "500", kanten: "" };

/* ─────────────────────────── Dialog ─────────────────────────── */

export function NewAnfrageDialog({
  open,
  state,
  setState,
  title = "Neue Anfrage",
  onClose,
  onSaveDraft,
  onSend,
}: {
  open: boolean;
  state: AnfrageWizardState;
  setState: React.Dispatch<React.SetStateAction<AnfrageWizardState>>;
  title?: string;
  onClose: () => void;
  onSaveDraft: () => void;
  onSend: () => void;
}) {
  const [snippetTemplates, setSnippetTemplates] = useState<AnfrageSnippetTemplate[]>([]);
  const [step, setStep] = useState<WizardStep>(1);
  const [stepError, setStepError] = useState<string | null>(null);
  const [blechFor, setBlechFor] = useState<number | null>(null);
  const [blech, setBlech] = useState<BlechForm>(EMPTY_BLECH);

  const hasGate = hasSupplierGate(state.recipients);
  const firstRecipient = state.recipients[0];
  const primarySupplierId =
    firstRecipient?.mode === "hero" ? firstRecipient.supplier?.id : null;
  const hasPositions = state.positionen.length > 0;
  const recipientError = validateTaRecipients(state.recipients);
  const positionsComplete =
    hasPositions && state.positionen.every((p) => (p.text || "").trim() !== "" && Number(p.menge) > 0);

  // Beim Öffnen: dort einsteigen, wo es weitergeht (Entwurf → Positionen).
  useEffect(() => {
    if (!open) return;
    setStepError(null);
    setBlechFor(null);
    setStep(hasSupplierGate(state.recipients) ? 2 : 1);
    // Nur beim Öffnen auswerten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    void listAnfrageSnippetTemplates().then((t) =>
      setSnippetTemplates(Array.isArray(t) ? t : []),
    );
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => persistLocalDraft(state), 400);
    return () => window.clearTimeout(t);
  }, [open, state]);

  // Schritt 2 braucht immer mindestens einen Auftrags-Platzhalter.
  useEffect(() => {
    if (!open || step !== 2) return;
    if (state.auftraege.length === 0 && state.placeholders === 0) {
      setState((s) => ({ ...s, placeholders: 1 }));
    }
  }, [open, step, state.auftraege.length, state.placeholders, setState]);

  /* ── Empfänger ── */

  function updateRecipient(id: number, patch: Partial<TaRecipient>) {
    setState((s) => ({
      ...s,
      recipients: s.recipients.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  }

  function setRecipientMode(id: number, mode: RecipientMode) {
    setState((s) => ({
      ...s,
      recipients: s.recipients.map((r) => {
        if (r.id !== id) return r;
        if (mode === "hero") {
          return { ...r, mode, manual: { firm: "", anrede: "", name: "", email: "" } };
        }
        return { ...r, mode, supplier: null, contact: null };
      }),
    }));
  }

  function addRecipient() {
    setState((s) => ({ ...s, recipients: [...s.recipients, createRecipient()] }));
  }

  function removeRecipient(id: number) {
    setState((s) => ({
      ...s,
      recipients: s.recipients.length > 1 ? s.recipients.filter((r) => r.id !== id) : s.recipients,
    }));
  }

  /* ── Aufträge / Positionen ── */

  function addAuftrag(a: AuftragMini) {
    setState((s) => {
      if (s.auftraege.some((x) => x.id === a.id)) return s;
      return {
        ...s,
        auftraege: [...s.auftraege, a],
        placeholders: Math.max(0, s.placeholders - 1),
      };
    });
  }

  function removeAuftrag(id: number) {
    setState((s) => {
      const auftraege = s.auftraege.filter((a) => a.id !== id);
      return {
        ...s,
        auftraege,
        positionen: s.positionen.filter((p) => Number(p.auftrag_id) !== Number(id)),
        placeholders: auftraege.length === 0 ? Math.max(1, s.placeholders) : s.placeholders,
      };
    });
  }

  function addPosition(auftragId: number, partial: Partial<DraftPosition>) {
    setState((s) => ({
      ...s,
      positionen: [
        ...s.positionen,
        {
          typ: "freitext",
          auftrag_id: auftragId,
          menge: 1,
          einheit: "",
          text: "",
          localId: newPositionLocalId(),
          ...partial,
        },
      ],
    }));
  }

  function updatePosition(localId: string, patch: Partial<DraftPosition>) {
    setState((s) => ({
      ...s,
      positionen: s.positionen.map((p) => (p.localId === localId ? { ...p, ...patch } : p)),
    }));
  }

  function removePosition(localId: string) {
    setState((s) => ({
      ...s,
      positionen: s.positionen.filter((p) => p.localId !== localId),
    }));
  }

  function submitBlech(auftragId: number) {
    const m = blech.material.trim() || "Blech";
    const parts = [m];
    if (blech.dicke.trim()) parts.push(`${blech.dicke.trim()} mm`);
    if (blech.laenge.trim() && blech.breite.trim()) parts.push(`${blech.laenge.trim()} x ${blech.breite.trim()} mm`);
    if (blech.kanten.trim()) parts.push(blech.kanten.trim());
    addPosition(auftragId, { typ: "freitext", text: parts.join(", ") });
    setBlechFor(null);
    setBlech(EMPTY_BLECH);
  }

  const snippetSelectValue = useMemo(() => {
    const cur = state.snippetTemplateId;
    if (cur == null) return "";
    return snippetTemplates.some((t) => t.id === cur) ? String(cur) : "";
  }, [state.snippetTemplateId, snippetTemplates]);

  const showMaContext = !!state.sourceMaId;

  /* ── Navigation ── */

  function goNext() {
    setStepError(null);
    if (step === 1) {
      if (recipientError) {
        setStepError(recipientError);
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!hasPositions) {
        setStepError("Bitte mindestens eine Position eintragen.");
        return;
      }
      if (!positionsComplete) {
        setStepError("Jede Position braucht einen Text und eine Menge größer 0.");
        return;
      }
      setStep(3);
    }
  }

  function goTo(target: WizardStep) {
    setStepError(null);
    if (target < step) {
      setStep(target);
      return;
    }
    if (target === 2 && !recipientError) setStep(2);
    if (target === 3 && !recipientError && positionsComplete) setStep(3);
  }

  const stepReachable = (s: WizardStep) =>
    s <= step || (s === 2 && !recipientError) || (s === 3 && !recipientError && positionsComplete);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={STEPS.find((s) => s.step === step)?.hint}
      size={showMaContext ? "4xl" : "2xl"}
      zIndexClass="z-[110]"
      fullScreenMobile
      dirty={hasPositions || hasGate}
      panelClassName={showMaContext ? "sm:max-h-[92vh]" : undefined}
      bodyClassName={
        showMaContext
          ? "!flex !min-h-0 !flex-col !py-3 max-sm:!overflow-y-auto max-sm:material-mobile-scroll lg:!overflow-hidden"
          : undefined
      }
      footer={
        <footer className="flex w-full flex-wrap items-center justify-between gap-2">
          <Button type="button" variant="ghost" onClick={onSaveDraft}>
            {hasPositions ? "Als Entwurf speichern" : "Abbrechen"}
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            {step > 1 ? (
              <Button type="button" variant="secondary" onClick={() => goTo((step - 1) as WizardStep)}>
                Zurück
              </Button>
            ) : null}
            {step < 3 ? (
              <Button type="button" data-tour="anfrage-weiter" onClick={goNext} disabled={step === 1 ? !hasGate : !hasPositions}>
                Weiter
              </Button>
            ) : (
              <Button
                type="button"
                onClick={onSend}
                disabled={!positionsComplete}
                leftIcon={<PaperAirplaneIcon className="h-4 w-4" aria-hidden />}
              >
                {anfrageSendLabel(state.recipients.length)}
              </Button>
            )}
          </div>
        </footer>
      }
    >
      <MaWizardSplitLayout sourceMaId={state.sourceMaId}>
        <ol className="mb-4 grid grid-cols-3 gap-1" aria-label="Schritte">
          {STEPS.map((s) => {
            const done = s.step < step;
            const active = s.step === step;
            const reachable = stepReachable(s.step);
            return (
              <li key={s.step}>
                <button
                  type="button"
                  disabled={!reachable}
                  onClick={() => goTo(s.step)}
                  className={clsx(
                    "flex w-full items-center gap-2 rounded-ios border px-2.5 py-2 text-start transition",
                    active
                      ? "border-accent bg-accent-soft/60"
                      : done
                        ? "border-separator bg-card hover:bg-fill-tertiary"
                        : "border-separator bg-card opacity-70",
                    !reachable && "cursor-default",
                  )}
                  aria-current={active ? "step" : undefined}
                >
                  <span
                    className={clsx(
                      "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                      done && "bg-accent text-label-on-accent",
                      active && "bg-accent text-label-on-accent",
                      !done && !active && "bg-fill-tertiary text-label-secondary",
                    )}
                  >
                    {done ? <CheckIcon className="h-3.5 w-3.5" aria-hidden /> : s.step}
                  </span>
                  <span className="min-w-0">
                    <span className={clsx("block truncate text-sm font-semibold", active ? "text-label" : "text-label-secondary")}>
                      {s.label}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        {stepError ? (
          <p className="mb-3 rounded-ios border border-status-red/30 bg-status-red/10 px-3 py-2 text-sm text-status-red" role="alert">
            {stepError}
          </p>
        ) : null}

        {/* ───────────── Schritt 1: Lieferant ───────────── */}
        {step === 1 ? (
          <section className="space-y-3" data-tour="anfrage-lieferant">
            {state.recipients.map((r, idx) => (
              <article key={r.id} className="rounded-ios border border-separator bg-card p-3">
                <header className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-label">
                    {state.recipients.length > 1 ? `Lieferant ${idx + 1}` : "Lieferant"}
                  </span>
                  {idx > 0 ? (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeRecipient(r.id)}>
                      Entfernen
                    </Button>
                  ) : null}
                </header>

                <SegmentedControl
                  value={r.mode}
                  onChange={(mode) => setRecipientMode(r.id, mode as RecipientMode)}
                  options={[
                    { value: "hero", label: "Aus Kontakten" },
                    { value: "manual", label: "E-Mail eingeben" },
                  ]}
                  name={`ta-rec-mode-${r.id}`}
                  className="mb-3"
                />

                {r.mode === "hero" ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <LockedAutocomplete<HeroSupplier>
                      label="Firma"
                      placeholder="Schüco, Marx, …"
                      minChars={0}
                      value={supplierOptionFromRecipient(r)}
                      loadOptions={loadOrderSupplierOptions}
                      onChange={(opt) => {
                        if (!opt) {
                          updateRecipient(r.id, { supplier: null, contact: null });
                          return;
                        }
                        updateRecipient(r.id, {
                          supplier: {
                            id: String(opt.value.id),
                            name: opt.value.name || "",
                            email: (opt.value.email || "").trim(),
                            geschaeftspartner_id: opt.value.geschaeftspartner_id ?? null,
                            has_logo: Boolean(opt.value.has_logo),
                            logo_updated_at: opt.value.logo_updated_at ?? null,
                          },
                          contact: null,
                        });
                      }}
                    />
                    <LockedAutocomplete<HeroSupplierContact>
                      label="Ansprechpartner (E-Mail-Empfänger)"
                      placeholder="Name eingeben …"
                      minChars={0}
                      disabled={!r.supplier?.id}
                      disabledHint="Zuerst die Firma wählen."
                      value={contactOptionFromRecipient(r)}
                      loadOptions={(q) =>
                        loadOrderSupplierContactOptions(recipientToSupplierState(r), q)
                      }
                      onChange={(opt) => {
                        if (!opt) {
                          updateRecipient(r.id, { contact: null });
                          return;
                        }
                        const c = opt.value;
                        const fields = supplierFieldsFromContact(c);
                        updateRecipient(r.id, {
                          contact: {
                            email: fields.email || "",
                            first_name: fields.contact_first_name || "",
                            last_name: fields.contact_last_name || "",
                            anrede: c.is_company_email ? fields.anrede || "" : anredeFromTitle(c.title || ""),
                            title: c.is_company_email ? "" : c.title || "",
                            is_company_email: !!c.is_company_email,
                          },
                        });
                      }}
                    />
                  </div>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <TextInput
                      label="Firma"
                      value={r.manual.firm}
                      onChange={(e) =>
                        updateRecipient(r.id, { manual: { ...r.manual, firm: e.target.value } })
                      }
                    />
                    <TextInput
                      label="E-Mail"
                      type="email"
                      value={r.manual.email}
                      onChange={(e) =>
                        updateRecipient(r.id, { manual: { ...r.manual, email: e.target.value } })
                      }
                    />
                    <TextInput
                      label="Anrede (optional)"
                      placeholder="Herr / Frau"
                      value={r.manual.anrede}
                      onChange={(e) =>
                        updateRecipient(r.id, { manual: { ...r.manual, anrede: e.target.value } })
                      }
                    />
                    <TextInput
                      label="Name (optional)"
                      value={r.manual.name}
                      onChange={(e) =>
                        updateRecipient(r.id, { manual: { ...r.manual, name: e.target.value } })
                      }
                    />
                    <p className="text-xs text-label-tertiary sm:col-span-2">
                      Ohne Anrede und Name wird „Sehr geehrte Damen und Herren“ verwendet.
                    </p>
                  </div>
                )}
              </article>
            ))}

            <div className="flex flex-wrap items-center justify-between gap-2 rounded-ios border border-dashed border-separator-opaque px-3 py-2">
              <p className="text-xs text-label-secondary">
                Gleiche Anfrage an mehrere Firmen? Jede Firma erhält eine eigene Mail und TA-Nummer – die Preise
                lassen sich danach direkt vergleichen.
              </p>
              <Button type="button" size="sm" variant="secondary" onClick={addRecipient}>
                + Weitere Firma
              </Button>
            </div>
          </section>
        ) : null}

        {/* ───────────── Schritt 2: Positionen ───────────── */}
        {step === 2 ? (
          <section className="space-y-3">
            {state.auftraege.map((a) => {
              const ta = state.taPreview[String(a.id)] || "TA-Nummer folgt";
              const positions = state.positionen.filter(
                (p) => Number(p.auftrag_id) === Number(a.id),
              );
              return (
                <article key={a.id} className="rounded-ios border border-separator bg-card p-3">
                  <header className="mb-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs text-label-secondary">Auftrag</p>
                      <h4 className="truncate font-semibold text-label">{formatAuftragHeadline(a)}</h4>
                      <span className="mt-1 inline-block rounded bg-fill-tertiary px-2 py-0.5 font-mono text-xs text-label-secondary">
                        {ta}
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label="Auftrag entfernen"
                      onClick={() => removeAuftrag(a.id)}
                    >
                      Entfernen
                    </Button>
                  </header>

                  {positions.length ? (
                    <ul className="mb-3 space-y-2">
                      {positions.map((p) => (
                        <li
                          key={p.localId}
                          className="grid gap-2 rounded-ios border border-separator bg-fill-quaternary/20 p-2 sm:grid-cols-[5rem_5.5rem_1fr_auto] sm:items-end"
                        >
                          <TextInput
                            label="Menge"
                            type="number"
                            min={0}
                            step="any"
                            inputClassName="!w-full"
                            value={String(p.menge)}
                            onChange={(e) =>
                              updatePosition(p.localId, { menge: Number(e.target.value) || 0 })
                            }
                          />
                          <NativeSelectField
                            label="Einheit"
                            selectSize="sm"
                            value={resolveMaterialUnit(p.einheit)}
                            onChange={(e) => updatePosition(p.localId, { einheit: e.target.value })}
                          >
                            {COMMON_MATERIAL_UNITS.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </NativeSelectField>
                          <TextInput
                            label="Artikel / Beschreibung"
                            inputClassName="!w-full"
                            placeholder="Was genau soll angefragt werden?"
                            value={p.text}
                            error={!(p.text || "").trim() ? "Text fehlt" : undefined}
                            onChange={(e) => updatePosition(p.localId, { text: e.target.value })}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="self-end text-status-red"
                            aria-label="Position entfernen"
                            onClick={() => removePosition(p.localId)}
                          >
                            <TrashIcon className="h-4 w-4" aria-hidden />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mb-3 text-xs text-label-tertiary">
                      Noch keine Position – Artikel suchen oder Freitext hinzufügen.
                    </p>
                  )}

                  <div className="flex flex-wrap items-end gap-2">
                    <Autocomplete<HeroArticleSuggestion>
                      className="min-w-[12rem] flex-1"
                      label={primarySupplierId ? "Artikel suchen (Nr., EAN, Bezeichnung)" : "Artikel suchen"}
                      placeholder={primarySupplierId ? "Leer lassen = meistbestellte Artikel dieses Lieferanten" : "Nr., EAN, Bezeichnung …"}
                      minChars={0}
                      clearOnSelect
                      loadOptions={async (q) => {
                        const supId = primarySupplierId;
                        if (!q.trim()) {
                          if (!supId) return [];
                          const top = await topOrderProducts(supId);
                          return top.map((m) => {
                            const heroNr = (m.hero_nr || "").trim();
                            const text = (m.text || "").trim();
                            const label = `${text || heroNr || "-"}${m.order_count ? ` · ${m.order_count}×` : ""}`;
                            const value: HeroArticleSuggestion = {
                              label: text || heroNr || "-",
                              value: heroNr || text,
                              artikel_id: null,
                              hero_nr: heroNr || null,
                              hero_base_name: text || null,
                              hero_unit: m.einheit || null,
                              einheit: m.einheit || null,
                              ean: m.hero_ean || null,
                              image_src: m.image_src || null,
                            };
                            return withArtikelImage({ key: heroNr || text, label, value });
                          });
                        }
                        const list = await searchHeroArticles(q);
                        return list.map((m) =>
                          withArtikelImage({
                            key: m.hero_nr || m.value,
                            label: m.label,
                            value: m,
                          }),
                        );
                      }}
                      onSelect={(opt) => {
                        if (!opt) return;
                        const m = opt.value;
                        addPosition(a.id, {
                          typ: "hero",
                          menge: 1,
                          einheit: resolveMaterialUnit(m.einheit || undefined),
                          text: heroArticleOrderText(m),
                          hero_nr: m.hero_nr || null,
                          hero_ean: m.ean || null,
                          hero_unit: m.einheit || null,
                        });
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="shrink-0"
                      onClick={() => addPosition(a.id, { typ: "freitext", text: "" })}
                    >
                      + Freitext
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={blechFor === a.id ? "primary" : "secondary"}
                      className="shrink-0"
                      onClick={() => {
                        setBlechFor((cur) => (cur === a.id ? null : a.id));
                        setBlech(EMPTY_BLECH);
                      }}
                    >
                      ▭ Blech
                    </Button>
                  </div>

                  {blechFor === a.id ? (
                    <div className="mt-2 rounded-ios border border-accent/30 bg-accent-soft/30 p-3">
                      <p className="mb-2 text-xs font-semibold text-label">Blechzuschnitt</p>
                      <div className="grid gap-2 sm:grid-cols-5">
                        <TextInput
                          label="Material / Farbe"
                          value={blech.material}
                          onChange={(e) => setBlech((b) => ({ ...b, material: e.target.value }))}
                        />
                        <TextInput
                          label="Dicke (mm)"
                          inputMode="decimal"
                          value={blech.dicke}
                          onChange={(e) => setBlech((b) => ({ ...b, dicke: e.target.value }))}
                        />
                        <TextInput
                          label="Länge (mm)"
                          inputMode="numeric"
                          value={blech.laenge}
                          onChange={(e) => setBlech((b) => ({ ...b, laenge: e.target.value }))}
                        />
                        <TextInput
                          label="Breite (mm)"
                          inputMode="numeric"
                          value={blech.breite}
                          onChange={(e) => setBlech((b) => ({ ...b, breite: e.target.value }))}
                        />
                        <TextInput
                          label="Kanten (optional)"
                          value={blech.kanten}
                          onChange={(e) => setBlech((b) => ({ ...b, kanten: e.target.value }))}
                        />
                      </div>
                      <div className="mt-2 flex justify-end gap-2">
                        <Button type="button" size="sm" variant="ghost" onClick={() => setBlechFor(null)}>
                          Abbrechen
                        </Button>
                        <Button type="button" size="sm" onClick={() => submitBlech(a.id)}>
                          Position hinzufügen
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}

            {Array.from({ length: state.placeholders }).map((_, i) => (
              <article key={`ph-${i}`} className="rounded-ios border border-dashed border-separator-opaque bg-card p-3">
                <h4 className="font-semibold text-label">
                  {state.auftraege.length === 0 ? "Für welchen Auftrag?" : "Weiterer Auftrag"}
                </h4>
                <p className="mb-2 text-xs text-label-secondary">
                  Auftragsnummer, Ort oder Beschreibung eingeben – die TA-Nummer wird automatisch vergeben.
                </p>
                <Autocomplete<AuftragMini>
                  placeholder="Auftrag suchen …"
                  minChars={0}
                  loadOptions={async (q) => {
                    const list = q.trim()
                      ? await searchAuftraege(q)
                      : await getRecentAuftraege({ limit: 15 });
                    return list.map((row) => ({
                      key: String(row.id),
                      label: formatAuftragLookupLabel(row),
                      value: {
                        id: row.id,
                        jahr: row.jahr ?? new Date().getFullYear(),
                        auftragsnummer: row.auftragsnummer,
                        ort: row.ort,
                        beschreibung: row.beschreibung,
                      },
                    }));
                  }}
                  onSelect={(opt) => {
                    if (opt?.value) addAuftrag(opt.value);
                  }}
                />
                {state.auftraege.length > 0 ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="mt-2"
                    onClick={() =>
                      setState((s) => ({ ...s, placeholders: Math.max(0, s.placeholders - 1) }))
                    }
                  >
                    Doch nicht
                  </Button>
                ) : null}
              </article>
            ))}

            {state.auftraege.length > 0 && state.placeholders === 0 ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="w-full"
                onClick={() => setState((s) => ({ ...s, placeholders: s.placeholders + 1 }))}
              >
                + Weiterer Auftrag in derselben Anfrage
              </Button>
            ) : null}
          </section>
        ) : null}

        {/* ───────────── Schritt 3: Prüfen & senden ───────────── */}
        {step === 3 ? (
          <section className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-ios border border-separator bg-card p-3">
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                    {state.recipients.length > 1 ? "Empfänger" : "Empfänger"}
                  </p>
                  <button type="button" className="text-xs text-accent hover:underline" onClick={() => goTo(1)}>
                    Ändern
                  </button>
                </div>
                <ul className="space-y-1.5">
                  {state.recipients.map((r) => {
                    const s = recipientSummary(r);
                    return (
                      <li key={r.id} className="min-w-0">
                        <p className="truncate text-sm font-medium text-label">{s.firm}</p>
                        <p className="truncate text-xs text-label-secondary">{s.contact}</p>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="rounded-ios border border-separator bg-card p-3">
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Positionen</p>
                  <button type="button" className="text-xs text-accent hover:underline" onClick={() => goTo(2)}>
                    Ändern
                  </button>
                </div>
                <ul className="space-y-2">
                  {state.auftraege.map((a) => {
                    const positions = state.positionen.filter((p) => Number(p.auftrag_id) === Number(a.id));
                    return (
                      <li key={a.id}>
                        <p className="truncate text-xs font-medium text-label-secondary">
                          {formatAuftragHeadline(a)}
                          {state.taPreview[String(a.id)] ? (
                            <span className="ms-1 font-mono text-label-tertiary">· {state.taPreview[String(a.id)]}</span>
                          ) : null}
                        </p>
                        <ul className="mt-0.5 space-y-0.5 text-sm text-label">
                          {positions.map((p) => (
                            <li key={p.localId} className="truncate">
                              {p.menge} {resolveMaterialUnit(p.einheit)} · {p.text}
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="rounded-ios border border-separator bg-card p-3">
              <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Mail-Vorschau</p>
                {snippetTemplates.length ? (
                  <NativeSelectField
                    label="Textvorlage"
                    selectSize="sm"
                    className="w-full sm:w-64"
                    value={snippetSelectValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      setState((s) => ({ ...s, snippetTemplateId: v ? Number(v) : null }));
                    }}
                  >
                    <option value="">Standardtext</option>
                    {snippetTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name || "Ohne Titel"}
                      </option>
                    ))}
                  </NativeSelectField>
                ) : null}
              </div>
              {state.mailBodyHtml || state.mailSubject || state.mailBody ? (
                <>
                  <p className="text-sm font-semibold text-label">{state.mailSubject || "(Betreff wird erzeugt …)"}</p>
                  {state.mailBodyHtml ? (
                    <div
                      className="eh-mail-html-editor mt-2 overflow-y-auto text-sm"
                      style={{ maxHeight: "22rem" }}
                      dangerouslySetInnerHTML={{ __html: state.mailBodyHtml }}
                    />
                  ) : (
                    <pre className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-ios bg-fill-quaternary/40 p-3 font-sans text-sm text-label">
                      {state.mailBody}
                    </pre>
                  )}
                </>
              ) : (
                <p className="text-sm text-label-secondary">Die Mail wird gerade aufgesetzt …</p>
              )}
              <p className="mt-2 text-xs text-label-tertiary">
                Im nächsten Fenster kannst du Empfänger, Betreff und Text noch ändern. Die Signatur wird wie bei
                Bestellungen angehängt. Dateien aus dem Projektordner kannst du dort auswählen und direkt über
                Microsoft oder über dein Mailprogramm senden.
              </p>
            </div>
          </section>
        ) : null}
      </MaWizardSplitLayout>
    </Dialog>
  );
}
